import {Component, Input, OnDestroy} from '@angular/core';
import {CustomMessageService, LocalStorageService} from "library";
import {ActivatedRoute, Router} from "@angular/router";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {UtilService} from "library";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {AuthService} from "../../../../services/api/v1/auth.service";
import {LocalStorageKey, RequestCancelReason} from "../../../../enums";
import {ResponseHttpError, ResponseHttpSuccess } from "library";
import {HttpStatusCode} from "@angular/common/http";
import {OptionPointSales} from "../../../../enums/option-point-sales";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";

// Constantes para títulos y mensajes
const TITLE_ANNULMENT = "Hemos iniciado el proceso para ANULAR la transacción";
const TITLE_OTP_EXPIRED = "El código anterior se ha vencido";
const SUB_TITLE_OTP = "Ahora, ve a la caja para digitar el OTP en el teléfono";
const SUB_TITLE_OTP_PROGRESS = "Estamos generando el OTP a usar. Por favor, espera, este proceso puede durar alrededor de 5 minutos.";
const TEXT_LOADING_PROGRESS = "Esperando OTP";
const TEXT_LOADING_WAIT = "Esperando confirmación";

// Delays para polling
const POLL_FIRST_DELAY = 120000; // 2 minutos
const POLL_RETRY_DELAY = 15000;  // 15 segundos

export enum OtpActionRetry {
  getOtp = 'getOtp',
  getStatus = 'getStatus',
  reGetOtp = 'reGetOtp'
}

@Component({
  selector: 'app-sales-cancel-request',
  templateUrl: './sales-cancel-request.component.html'
})
export class SalesCancelRequestComponent implements OnDestroy {
  @Input() type!: string;
  @Input() requestIncompletedId!: string;
  @Input() isNavigate = true;
  @Input() reloadOnCancel = false;
  requestId: string;
  visibleCancelRequestModal: boolean = false;
  cancelOTPRequest: boolean = true;

  // Estados para flujo OTP
  showOtpModal: boolean = false;
  showScreenInit: boolean = false;
  showScreenRetry: boolean = false;
  showScreenCancel: boolean = false;

  // Datos OTP
  codeOtp: string = '';
  titleInit: string = TITLE_ANNULMENT;
  subTitleInit: string = SUB_TITLE_OTP;
  textLoading: string = TEXT_LOADING_WAIT;
  pollActiveStatus: boolean = false;
  otpActionRetry: OtpActionRetry = OtpActionRetry.getOtp;

  // Timer de reintento
  timerActive: boolean = false;
  minutes: number = 2;
  seconds: number = 0;
  private timerInterval: any;
  private timeouts: any[] = [];
  private destroy$ = new Subject<void>();

  constructor(private utilService: UtilService,
              private requestService: RequestService,
              private customMessageService: CustomMessageService,
              private route: ActivatedRoute,
              private salesWorkflowService: SalesWorkflowService,
              private authService: AuthService,
              private localStorageService: LocalStorageService) {
    this.requestId = this.route.snapshot.params['request_id']
  }

  /**
   * Inicia el proceso de cancelación
   * Detecta si requiere flujo OTP (Claro Credibanco) o cancelación directa
   */
  onCancelRequest() {
    this.visibleCancelRequestModal = false;
    this.requestId ??= this.requestIncompletedId;

    const storeConfig = this.localStorageService.get(LocalStorageKey.data)?.store?.setting;
    const typePointSales = storeConfig?.type_point_sales;

    if (typePointSales === OptionPointSales['claro-credibanco']) {
      // Flujo con OTP para Claro Credibanco
      this.showOtpModal = true;
      this.showScreenInit = true;
      this.getOtp();
    } else {
      // Flujo directo sin OTP
      this.utilService.enablePageLoading();
      this.cancelRequestDirect();
    }
  }

  /**
   * Genera OTP para cancelación
   * POST /request/{id}/point-of-sales/otp con cancelar: true
   */
  getOtp() {
    this.requestService.generateOtpPointOfSales(this.requestId, this.cancelOTPRequest)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (resp: ResponseHttpSuccess) => {
        switch (resp.status) {
          case HttpStatusCode.Ok:
            // OTP ya fue utilizado → cancelar directo
            this.closeAllScreens();
            this.stopPollStatus();
            this.cancelRequestDirect();
            break;
          case HttpStatusCode.Accepted:
            // Recibe otp_code_anular → mostrar e iniciar polling
            this.closeAllScreens();
            this.titleInit = TITLE_ANNULMENT;
            this.subTitleInit = SUB_TITLE_OTP;
            this.codeOtp = resp.body.data.otp_code_anular;
            this.textLoading = TEXT_LOADING_WAIT;
            this.showScreenInit = true;
            // Iniciar polling después de 15 segundos
            const timeoutId = setTimeout(() => this.observableStatusOtp(), POLL_RETRY_DELAY);
            this.timeouts.push(timeoutId);
            break;
          case HttpStatusCode.PartialContent:
            // En progreso → reintentar en 15 segundos
            this.closeAllScreens();
            this.textLoading = TEXT_LOADING_PROGRESS;
            this.titleInit = TITLE_ANNULMENT;
            this.subTitleInit = SUB_TITLE_OTP_PROGRESS;
            this.showScreenInit = true;
            const retryId = setTimeout(() => this.getOtp(), POLL_RETRY_DELAY);
            this.timeouts.push(retryId);
            break;
          default:
            console.error("Código de respuesta no esperado en getOtp:", resp.status);
            break;
        }
      },
      error: (error: ResponseHttpError) => {
        switch (error.status) {
          case HttpStatusCode.NotAcceptable:
            // Error → pantalla de reintento con timer
            this.closeAllScreens();
            this.showScreenRetry = true;
            this.otpActionRetry = OtpActionRetry.getOtp;
            this.startRetryTimer();
            break;
          case HttpStatusCode.Conflict:
            // Bloqueado → pantalla de cancelación
            this.closeAllScreens();
            this.showScreenCancel = true;
            break;
          default:
            console.error("Error inesperado en getOtp:", error.status);
            this.customMessageService.comunicationError();
            this.closeModal();
            break;
        }
      }
    });
  }

  /**
   * Polling del estado del OTP
   * GET /request/{id}/point-of-sales/otp/status
   */
  observableStatusOtp() {
    let attempt = 0;
    this.pollActiveStatus = true;

    const poll = () => {
      if (!this.pollActiveStatus) return;

      this.requestService.getStatusOtpPointOfSales(this.requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: ResponseHttpSuccess) => {
          switch (resp.status) {
            case HttpStatusCode.Accepted:
              // OTP confirmado → cancelar solicitud
              this.closeAllScreens();
              this.stopPollStatus();
              this.cancelRequestDirect();
              break;
            case HttpStatusCode.PartialContent:
              // En progreso → seguir polling
              if (this.pollActiveStatus) {
                attempt++;
                const nextDelay = attempt === 1 ? POLL_FIRST_DELAY : POLL_RETRY_DELAY;
                const timeoutId = setTimeout(poll, nextDelay);
                this.timeouts.push(timeoutId);
              }
              break;
            default:
              console.error("Código de respuesta no esperado en status:", resp.status);
          }
        },
        error: (error: ResponseHttpError) => {
          this.stopPollStatus();
          switch (error.status) {
            case HttpStatusCode.NotAcceptable:
              // Error → pantalla de reintento
              this.closeAllScreens();
              this.showScreenRetry = true;
              this.otpActionRetry = OtpActionRetry.getStatus;
              this.startRetryTimer();
              break;
            case HttpStatusCode.UnprocessableEntity:
              // OTP vencido → regenerar
              this.reGetOtp();
              break;
            default:
              console.error("Error inesperado en status:", error.status);
              this.customMessageService.comunicationError();
              this.closeModal();
          }
        }
      });
    };
    poll();
  }

  /**
   * Regenera OTP cuando el anterior ha vencido
   * POST /request/{id}/point-of-sales/otp/regenerate
   */
  reGetOtp() {
    this.requestService.reGenerateOtpPointOfSales(this.requestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: ResponseHttpSuccess) => {
          switch (resp.status) {
            case HttpStatusCode.Created:
              // Nuevo OTP generado → mostrar y reiniciar polling
              this.closeAllScreens();
              this.titleInit = TITLE_OTP_EXPIRED;
              this.subTitleInit = SUB_TITLE_OTP;
              this.textLoading = TEXT_LOADING_WAIT;
              this.codeOtp = resp.body.data.otp_code;
              this.showScreenInit = true;
              this.observableStatusOtp();
              break;
            default:
              console.error("Código de respuesta no esperado en reGetOtp:", resp.status);
              break;
          }
        },
        error: (error: ResponseHttpError) => {
          switch (error.status) {
            case HttpStatusCode.NotAcceptable:
              // Error → pantalla de reintento
              this.closeAllScreens();
              this.showScreenRetry = true;
              this.otpActionRetry = OtpActionRetry.reGetOtp;
              this.startRetryTimer();
              break;
            case HttpStatusCode.Conflict:
              // Bloqueado → pantalla de cancelación
              this.closeAllScreens();
              this.showScreenCancel = true;
              break;
            default:
              console.error("Error inesperado en reGetOtp:", error.status);
              this.customMessageService.comunicationError();
              this.closeModal();
          }
        }
      });
  }

  /**
   * Reintenta la última acción fallida
   */
  onRetry() {
    this.closeAllScreens();
    this.showScreenInit = true;
    switch (this.otpActionRetry) {
      case OtpActionRetry.getOtp:
        this.getOtp();
        break;
      case OtpActionRetry.reGetOtp:
        this.reGetOtp();
        break;
      case OtpActionRetry.getStatus:
        this.observableStatusOtp();
        break;
    }
  }

  /**
   * Inicia el temporizador de reintento (2 minutos)
   */
  startRetryTimer() {
    this.timerActive = true;
    this.minutes = 2;
    this.seconds = 0;

    this.timerInterval = setInterval(() => {
      if (this.seconds === 0) {
        if (this.minutes === 0) {
          this.stopRetryTimer();
          return;
        }
        this.minutes--;
        this.seconds = 59;
      } else {
        this.seconds--;
      }
      if (this.minutes === 0 && this.seconds === 0) {
        this.stopRetryTimer();
      }
    }, 1000);
  }

  /**
   * Detiene el temporizador de reintento
   */
  stopRetryTimer() {
    this.timerActive = false;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /**
   * Cierra todas las pantallas del sidebar
   */
  closeAllScreens() {
    this.showScreenInit = false;
    this.showScreenRetry = false;
    this.showScreenCancel = false;
  }

  /**
   * Detiene el polling del estado
   */
  stopPollStatus() {
    this.pollActiveStatus = false;
  }

  /**
   * Cierra el modal y limpia estados
   */
  closeModal() {
    this.showOtpModal = false;
    this.closeAllScreens();
    this.stopPollStatus();
    this.stopRetryTimer();
  }

  /**
   * Cancelación directa de la solicitud (sin OTP o después de OTP confirmado)
   */
  cancelRequestDirect() {
    const reasonText = this.authService.isAuthenticated()
      ? RequestCancelReason.SELLER_CANCELS
      : RequestCancelReason.CUSTOMER_CANCELS;

    this.requestService.cancel(this.requestId, reasonText).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        this.closeModal();
        this.utilService.disablePageLoading();

        if (resp.status === HttpStatusCode.NoContent) {
          this.customMessageService.successHandler({
            title: 'Éxito',
            description: 'La solicitud ha sido cancelada con éxito.',
            buttonTitle: 'Volver al inicio',
            icon: true,
            iconPos: 'left',
            callback: () => {
              if (this.isNavigate) {
                this.salesWorkflowService.navigateToInitial();
              } else if (this.reloadOnCancel) {
                window.location.reload();
              }
            }
          });
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (error: ResponseHttpError) => {
        this.customMessageService.responseNoticeHandler(error);
        this.closeModal();
        this.utilService.disablePageLoading();
      }
    });
  }

  /**
   * Limpieza de recursos al destruir el componente
   */
  ngOnDestroy() {
    this.stopPollStatus();
    this.stopRetryTimer();

    // Limpiar timeouts pendientes
    if (this.timeouts && this.timeouts.length) {
      this.timeouts.forEach(id => clearTimeout(id));
      this.timeouts = [];
    }

    // Completar Subject para cancelar suscripciones
    this.destroy$.next();
    this.destroy$.complete();
  }
}
